import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import Tooltip from '@material-ui/core/Tooltip'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { sum } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineFileExcel } from 'react-icons/ai'

import { Fees, PenaltyScheme, Provider } from 'src/api/gen/api'
import { CurrencyKind } from 'src/api/types/units'
import { useCurrencyFormatter } from 'src/layout/AgencySettings'
import { useProviders } from 'src/layout/hooks'
import { colors } from 'src/theme'

import FeeSummaryTable, { ACCORDION_ROW_HEIGHT, getProrationsLineCount } from './FeeSummaryTable'

export const useAccordionAmount = function (amount: number): string {
  const currencyFormatter = useCurrencyFormatter(CurrencyKind.Global)

  return currencyFormatter(Math.floor(amount))
}

export const useProvidersLogo = function (): Map<string, string> {
  const { allProviders } = useProviders()

  const providerLogos = new Map<string, string>(
    allProviders.map((provider: Provider) => [provider.id, provider.logo ?? '/img/img-placeholder.svg'])
  )

  return providerLogos
}

export const getAccordionDetailsHeight = function (fees: Fees[]): number {
  const prorationsCount = sum(fees.map((fee) => getProrationsLineCount(fee.prorations ?? {}) + 1))

  return ACCORDION_ROW_HEIGHT * prorationsCount
}

const useStyles = makeStyles((theme: Theme) => {
  const fontSize = '18px'
  return createStyles({
    accordionDetails: {
      maxHeight: '65vh',
    },
    amount: {
      fontWeight: 'bold',
      fontSize,
      margin: theme.spacing(),
    },
    amountSubtitle: {
      color: colors.grey,
      fontSize,
      margin: theme.spacing(),
    },
    expandIcon: {
      fontSize: 30,
    },
    exportContainer: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: theme.spacing(2),
    },
    exportIcon: {
      fontSize: 30,
      cursor: 'pointer',
      color: '#192233',
    },
    media: {
      textAlign: 'center',
      height: 100,
      width: 100,
      margin: 20,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    providerName: {
      color: theme.palette.primary.main,
      fontSize,
      fontWeight: 'bold',
      margin: theme.spacing(),
    },
    tooltip: {
      fontSize: 13,
      margin: theme.spacing(2),
    },
  })
})

export interface IProviderInfo {
  amount: number
  data: Fees[]
  fees_count: number
  providerId: string
  providerName: string
}

interface IProps {
  fees_scheme: PenaltyScheme
  info: IProviderInfo
  onClickExport: (event: React.MouseEvent<HTMLElement>) => void
}

const FeeSummaryAccordion = (props: IProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const amount = useAccordionAmount(props.info.amount)
  const providerLogos = useProvidersLogo()

  const fees = props.info.data

  const onExportClick = function (event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation()
    return props.onClickExport(event)
  }

  return (
    <Accordion style={{ borderRadius: 'inherit' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.expandIcon} />}>
        <div
          style={{
            backgroundImage: `url(${providerLogos.get(props.info.providerId)})`,
          }}
          className={classes.media}
        />
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignContent: 'center' }}>
          <div className={classes.providerName}>{props.info.providerName}</div>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'left' }}>
            <div className={classes.amount}>{amount}</div>
            <div className={classes.amountSubtitle}>{t('on filtered selection')}</div>
          </div>
        </div>
        <div className={classes.exportContainer}>
          <Tooltip title={t('common.export') as string} placement="bottom" classes={{ tooltip: classes.tooltip }}>
            <IconButton onClick={onExportClick}>
              <AiOutlineFileExcel className={classes.exportIcon} />
            </IconButton>
          </Tooltip>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ marginBottom: 10 }} className={classes.accordionDetails}>
        <FeeSummaryTable data={fees} totalCount={fees.length} fees_scheme={props.fees_scheme} />
      </AccordionDetails>
    </Accordion>
  )
}

export default FeeSummaryAccordion
